import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Clock, Shield, Mail, CheckCircle, LogOut } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface PortalUser {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  company_name: string;
  status: string;
  created_at: string;
}

const PendingApprovalPage = () => {
  const [portalUser, setPortalUser] = useState<PortalUser | null>(null);

  useEffect(() => {
    fetchPortalUser();
  }, []);

  const fetchPortalUser = async () => {
    try {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) return;

      const { data, error } = await supabase
        .from('portal_users')
        .select('*')
        .eq('id', user.user.id)
        .single();

      if (error) throw error;
      setPortalUser(data);

      // If user is now approved, redirect to devices page
      if (data.status === 'approved') {
        window.location.href = '/portal/mydevices';
      }
    } catch (error) {
      console.error('Error fetching portal user:', error);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    window.location.href = '/portal/login';
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card">
        <div className="container mx-auto px-2 sm:px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <img 
                src="/lovable-uploads/5b1ac4fa-502a-48e9-b8b6-7c1fe37496e8.png" 
                alt="smplify Logo" 
                className="h-6 sm:h-8"
              />
            </div>
            <Button variant="outline" size="sm" onClick={handleLogout}>
              <LogOut className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-2 sm:px-4 py-8 sm:py-16 max-w-2xl">
        <div className="text-center mb-6 sm:mb-8">
          <Clock className="h-12 w-12 sm:h-16 sm:w-16 text-primary mx-auto mb-4" />
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-2">Account Pending Approval</h1>
          <p className="text-muted-foreground">
            Your portal account is waiting for administrator approval
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Mail className="h-5 w-5 mr-2" />
              Approval Status
            </CardTitle>
            <CardDescription>
              We've received your registration and it's currently under review
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-6">
            {portalUser && (
              <div className="bg-muted p-4 rounded-lg">
                <h3 className="font-semibold mb-2">Account Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-muted-foreground">Name:</span> {portalUser.first_name} {portalUser.last_name}
                  </div>
                  <div>
                    <span className="text-muted-foreground">Email:</span> {portalUser.email}
                  </div>
                  <div>
                    <span className="text-muted-foreground">Company:</span> {portalUser.company_name}
                  </div>
                  <div>
                    <span className="text-muted-foreground">Status:</span> 
                    <span className="ml-1 capitalize font-medium text-orange-600">
                      {portalUser.status.replace('_', ' ')}
                    </span>
                  </div>
                </div>
              </div>
            )}

            <Alert>
              <CheckCircle className="h-4 w-4" />
              <AlertDescription>
                <strong>What happens next:</strong>
                <ol className="list-decimal list-inside mt-2 space-y-1">
                  <li>An administrator will review your account details</li>
                  <li>You'll receive an email notification once approved</li>
                  <li>After approval, you can log in and start enrolling devices</li>
                  <li>Device enrollment will also require individual approval</li>
                </ol>
              </AlertDescription>
            </Alert>

            <Alert>
              <AlertDescription>
                <strong>Typical approval time:</strong> 1-2 business days
                <br />
                <strong>Need help?</strong> Contact your administrator or IT support team
              </AlertDescription>
            </Alert>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Button onClick={fetchPortalUser} variant="outline">
                Check Status
              </Button>
              <Button onClick={handleLogout} variant="outline">
                Logout
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="text-center mt-8">
          <p className="text-sm text-muted-foreground">
            Questions about the approval process?{' '}
            <a href="mailto:support@smplify.app" className="text-primary hover:underline">
              Contact Support
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PendingApprovalPage;